import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { ResizeObserver } from '@juggle/resize-observer';
import { MUSIC_EVENTS, SLIDESHOW_AUDIO_FADE_OUT_MS } from 'Sp/Angular/Events/Client/Gallery';
import {
    ISlideshowPlayerTickerHandle,
    SlideshowPlayerTicker
} from 'ts/common/components/slideshows';
import type { ICoverSlideshowContextValue } from 'ts/common/contexts/CoverSlideshowContext';
import { computeFocalPointImageValues } from 'ts/common/utils';

interface Props {
    /** The already-loaded cover slideshow, from `CoverSlideshowContext`. */
    coverSlideshow: ICoverSlideshowContextValue;
    spEventBus: SpAngularJs.ISpEventBus;
}

/** The background always crops around center — it has no configurable focal point. */
const CENTER_FOCAL_POINT = { x: 50, y: 50 };
const DEFAULT_ASPECT_RATIO = '16:9';

/**
 * Renders a slideshow cover as an autoplaying, always-looping, cover-cropped background —
 * used by `CoverPhoto` when the gallery's cover is a slideshow rather than a static photo.
 *
 * Reuses `SlideshowPlayerTicker` (preloading, RAF clock, in-sync music via
 * `useSlideshowAudio`) in a chromeless "background mode": no controls, no play button,
 * `aria-hidden`, always cover-crops (ignoring the slideshow's own aspect ratio) instead of
 * letterboxing, and always loops regardless of the slideshow's `loopToFitMusic` setting —
 * covers keep playing indefinitely, they never have a natural "end".
 *
 * The slideshow arrives already fetched and image-preloaded (see `CoverSlideshowContext`), so
 * this starts playing on its first frame — the intro page's own loading state is what covered
 * the wait, and no first-slide poster is shown in between.
 *
 * Having no controls of its own, this background takes its audio direction entirely from the
 * gallery's shared music control (`<sp-music>`, rendered on the intro page for exactly this
 * reason): it plays its song muted — which is also what lets it start without a user gesture —
 * and unmutes when the visitor turns audio on there. Muting never stops the song, so it stays on
 * the slideshow's timeline and unmuting picks up in sync with the slides rather than restarting.
 * Gallery playlist music never plays over it: the intro page defers the playlist entirely (see
 * the route config's `musicDeferred`), and it starts — from the top — once the visitor leaves
 * this page for the gallery.
 */
const CoverSlideshowBackground: FC<Props> = ({ coverSlideshow, spEventBus }) => {
    const { slideshow, watermarkUrl, volume, isMuted, handleAudioActiveChange, isSilent } =
        coverSlideshow;

    const tickerRef = useRef<ISlideshowPlayerTickerHandle>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const [containerSize, setContainerSize] = useState<{ width: number; height: number }>();
    // Starts true: the slideshow is fully loaded before this ever mounts, so there is nothing to
    // wait for and no reason to fade in over the poster.
    const [isPlaying, setIsPlaying] = useState(true);

    // Measure the container via a callback ref rather than an effect, so measurement is tied to
    // the node actually mounting rather than to effect ordering. ResizeObserver delivers an
    // initial callback on observe(), which seeds containerSize immediately.
    const setContainerNode = useCallback((element: HTMLDivElement | null) => {
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = null;

        if (!element) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                setContainerSize({
                    width: element.offsetWidth,
                    height: element.offsetHeight
                });
            });
        });

        resizeObserver.observe(element);
        resizeObserverRef.current = resizeObserver;
    }, []);

    // Covers always loop, regardless of the slideshow's own `loopToFitMusic` setting. Some
    // slideshows have `loopToFitMusic: false`, in which case the ticker calls `onPlaybackEnd`
    // once and stops advancing; re-seek to the start instead so the background never goes static.
    const handlePlaybackEnd = useCallback(() => {
        tickerRef.current?.seek(0);
    }, []);

    // Tracks whether this background's audio is currently claiming exclusive playback, so the
    // GALLERY_PLAYING mirror below knows whether to react.
    const isAudioActiveRef = useRef(false);

    const handleAudioActiveChangeAndTrack = useCallback(
        (isActive: boolean) => {
            isAudioActiveRef.current = isActive;
            handleAudioActiveChange(isActive);
        },
        [handleAudioActiveChange]
    );

    // Mirror image of the duck in `useClientSlideshow`: if the visitor (re)starts gallery
    // background music while this background's own music is playing, stop this background
    // instead of playing both at once. Deliberately does not auto-resume when gallery music later
    // pauses — only *starting* one side should affect the other, never resuming it.
    //
    // A silent background (the gallery/album header) never claims audio in the first place, so
    // it must never be stopped by this duck either — bailing out here (rather than relying on
    // isAudioActiveRef staying false) documents that invariant directly and keeps hook order
    // stable across renders.
    useEffect(() => {
        if (isSilent) {
            return;
        }

        return spEventBus.on(MUSIC_EVENTS.GALLERY_PLAYING, () => {
            if (isAudioActiveRef.current) {
                setIsPlaying(false);
            }
        });
    }, [spEventBus, isSilent]);

    const aspectRatio = slideshow.aspectRatio || DEFAULT_ASPECT_RATIO;
    const [arW, arH] = aspectRatio.split(':').map(Number);

    // Cover-crop: reuses the same "background-size: cover" math as CoverPhoto's own
    // focal-point background image, centered, so the slideshow fills the hero box
    // regardless of its own aspect ratio — cropping overflow instead of letterboxing like
    // the ClientSlideshowViewer lightbox.
    const coverBounds = useMemo(() => {
        if (!containerSize || !containerSize.width || !containerSize.height) {
            return undefined;
        }

        return computeFocalPointImageValues(
            containerSize.width,
            containerSize.height,
            arW,
            arH,
            CENTER_FOCAL_POINT
        );
    }, [containerSize, arW, arH]);

    return (
        <Box
            ref={setContainerNode}
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            overflow="hidden"
            pointerEvents="none"
            aria-hidden="true"
            data-testid="cover-slideshow-background"
        >
            {coverBounds && (
                <Box
                    position="absolute"
                    top={`${coverBounds.offsetY}px`}
                    left={`${coverBounds.offsetX}px`}
                    width={`${coverBounds.width}px`}
                    height={`${coverBounds.height}px`}
                >
                    <SlideshowPlayerTicker
                        ref={tickerRef}
                        slideshow={slideshow}
                        isPlaying={isPlaying}
                        onPlaybackEnd={handlePlaybackEnd}
                        watermarkUrl={watermarkUrl}
                        // A silent background (the gallery/album header) never reports audio-active
                        // at all, rather than reporting it and relying on a no-op handler — that
                        // way `useSlideshowAudio` never emits MUSIC_EVENTS.MUTE for it in the first
                        // place, so it can never trigger the GALLERY_PLAYING duck above.
                        onAudioActiveChange={isSilent ? undefined : handleAudioActiveChangeAndTrack}
                        loadingIndicator={null}
                        volume={isSilent ? 0 : volume}
                        muted={isSilent || isMuted}
                        // Leaving the intro page for the gallery unmounts this, handing audio over
                        // to the gallery playlist — fade this song out rather than cutting it, and
                        // spMusic waits the same duration before starting the playlist so the two
                        // never overlap. A silent background has no audio to fade.
                        audioFadeOutOnTeardownMs={isSilent ? 0 : SLIDESHOW_AUDIO_FADE_OUT_MS}
                    />
                </Box>
            )}
        </Box>
    );
};

export default CoverSlideshowBackground;
